import { useCallback, useEffect, useState } from "react";
import { NFeDto, NFeFiltrarDto, STORAGENFE_KEY, getLocal, updateLocal } from "./NFe.Dto";
import useFilterManager from "src/hook/useFilterManager";
import { MapearErrosServidor } from "src/hook/Functions";
import {
  NFeBuscarXML,
  NFeConsultarSituacao,
  NFeEnviar,
  NFeExcluir,
  NFeFiltrar,
  NFeImprimir,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { useNavigate } from "react-router-dom";
import NFeMenuCode from "./NFe.Menu.Code";
import moment from "moment";

const getDataInicialPadrao = () => moment().startOf("month").toDate();
const getDataFinalPadrao = () => moment().toDate();

const parseFilterDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const m = moment(dateString);
  return m.isValid() ? m.toDate() : null;
};

const areDatesSameDay = (d1?: Date | null, d2?: Date | null) => {
  if (!d1 && !d2) return true;
  if (!d1 || !d2) return false;
  return moment(d1).isSame(d2, "day");
};

const NFeCode = () => {
  const [data, setData] = useState<NFeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalShowPropriedades, setModalShowPropriedades] = useState(false);
  const [modalNovoShow, setModalNovoShow] = useState(false);
  const [nfe, setNFe] = useState<NFeDto>();
  const navigate = useNavigate();

  const handleNovaNFe = useCallback(() => {
    const nfeSalva = getLocal<NFeDto>(STORAGENFE_KEY);
    if (nfeSalva && Object.keys(nfeSalva).length > 0) {
      setModalNovoShow(true);
    } else {
      navigate("/fiscal/nfe/cadastro");
    }
  }, [navigate]);

  const menu = NFeMenuCode({ setLoading, data, onNovaNFe: handleNovaNFe });

  const useFilter = useFilterManager<NFeFiltrarDto>({
    codigoStatusEnvio: null,
    dataEmissaoInicial: getDataInicialPadrao(),
    dataEmissaoFinal: getDataFinalPadrao(),
    serie: null,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      setLoading(true);
      try {
        await filtrar(useFilter.filters);
      } finally {
        setLoading(false);
      }
    };

    const hasValidDateRange =
      (useFilter.filters.dataEmissaoInicial &&
        useFilter.filters.dataEmissaoFinal) ||
      (!useFilter.filters.dataEmissaoInicial &&
        !useFilter.filters.dataEmissaoFinal);

    if (hasValidDateRange) {
      loadData();
    }

    return () => controller.abort();
  }, [useFilter.filters]);

  useEffect(() => {
    const serieParam = useFilter.searchParams.get("serie");
    const dataInicialParam = useFilter.searchParams.get("dataEmissaoInicial");
    const dataFinalParam = useFilter.searchParams.get("dataEmissaoFinal");
    const semDataParam = useFilter.searchParams.get("semData");

    const isInitialEmpty =
      !dataInicialParam && !dataFinalParam && semDataParam !== "true";

    const dataEmissaoInicial =
      semDataParam === "true"
        ? null
        : isInitialEmpty
        ? getDataInicialPadrao()
        : parseFilterDate(dataInicialParam);

    const dataEmissaoFinal =
      semDataParam === "true"
        ? null
        : isInitialEmpty
        ? getDataFinalPadrao()
        : parseFilterDate(dataFinalParam);

    const newFilters: NFeFiltrarDto = {
      codigoStatusEnvio:
        useFilter.searchParams.get("codigoStatusEnvio") || null,
      dataEmissaoInicial,
      dataEmissaoFinal,
      serie:
        serieParam && Number(serieParam) > 0 ? Number(serieParam) : null,
    };

    useFilter.setFilters((prev) => {
      if (
        prev.codigoStatusEnvio === newFilters.codigoStatusEnvio &&
        prev.serie === newFilters.serie &&
        areDatesSameDay(prev.dataEmissaoInicial, newFilters.dataEmissaoInicial) &&
        areDatesSameDay(prev.dataEmissaoFinal, newFilters.dataEmissaoFinal)
      ) {
        return prev;
      }
      return newFilters;
    });
  }, [useFilter.searchParams, useFilter.setFilters]);

  const filtrar = async (input: NFeFiltrarDto) => {
    try {
      const response = await NFeFiltrar(input);

      if (response.data.success) {
        setData(response.data.data);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao filtrar:", error);
      showToast({
        message: "Ocorreu erro ao buscar dados",
        title: "Erro",
        variant: "Danger",
      });
    }
  };

  const handleSelectStatus = (eventKey?: string | null) => {
    useFilter.updateFilter(
      "codigoStatusEnvio",
      eventKey === "99" ? null : eventKey
    );
  };

  const handleChangeSerie = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, "");
    useFilter.updateFilter(
      "serie",
      rawValue !== "" && Number(rawValue) > 0 ? Number(rawValue) : null
    );
  };

  const handleSetDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (!start && !end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: null,
        dataEmissaoFinal: null,
      }));
      useFilter.setMultipleFilters({
        dataEmissaoInicial: null,
        dataEmissaoFinal: null,
        semData: "true",
      } as any);
    } else if (start && !end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: start,
        dataEmissaoFinal: null,
      }));
    } else if (start && end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: start,
        dataEmissaoFinal: end,
      }));
      useFilter.setMultipleFilters({
        dataEmissaoInicial: moment(start).format("YYYY-MM-DD"),
        dataEmissaoFinal: moment(end).format("YYYY-MM-DD"),
        semData: null,
      } as any);
    }
  };

  // Utilitários de formatação
  const formatDate = (date: any) =>
    date ? new Date(date).toLocaleDateString("pt-BR") : "Selecionar";

  const getDatePickerTitle = () => {
    const { dataEmissaoInicial, dataEmissaoFinal } = useFilter.filters;

    if (!dataEmissaoInicial || !dataEmissaoFinal) {
      if (dataEmissaoInicial && !dataEmissaoFinal) {
        return `${formatDate(dataEmissaoInicial)} - Selecionar`;
      }
      return "Emissão";
    }

    return `${formatDate(dataEmissaoInicial)} - ${formatDate(
      dataEmissaoFinal
    )}`;
  };

  const onReturnModalPropriedades = () => {
    setModalShowPropriedades(false);
  };

  const onDeleteModalPropriedades = async (value: NFeDto) => {
    setLoading(true);
    await NFeExcluir(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.log(error);
        showToast({
          message: "Ocorreu erro ao excluir NFCe",
          title: "Erro",
          variant: "Danger",
        });
        return undefined;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onPrintModalPropriedades = async (value: NFeDto) => {
    onReturnModalPropriedades();
    setLoading(true);
    await NFeImprimir(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfURL = URL.createObjectURL(pdfBlob);
          window.open(pdfURL, "_blank");
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .finally(() => setLoading(false));
  };

  const onFetchStatusModalPropriedades = async (value: NFeDto) => {
    onReturnModalPropriedades();
    setLoading(true);
    await NFeConsultarSituacao(value.id)
      .then((response) => {
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        showToast({
          message: "Ocorreu erro ao consultar situação",
          title: "Erro",
          variant: "Danger",
        });
        return undefined;
      })
      .finally(() => setLoading(false));
  };

  const onSendModalPropriedades = async (value: NFeDto) => {
    onReturnModalPropriedades();
    setLoading(true);
    await NFeEnviar(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
          updateLocal<NFeDto>(STORAGENFE_KEY, null);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        showToast({
          message: "Ocorreu erro ao consultar situação",
          title: "Erro",
          variant: "Danger",
        });
        return undefined;
      })
      .finally(() => setLoading(false));
  };

  const onDownloadXml = async (value: NFeDto) => {
    setLoading(true);
    setModalShowPropriedades(false);

    try {
      const nomeArquivo = `NFCe_${value.numeroNF}`;
      const response = await NFeBuscarXML([value.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast({
        message: "Ocorreu erro ao baixar XML",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClickPropriedades = (value: NFeDto) => () => {
    setModalShowPropriedades(true);
    setNFe(value);
  };

  const onEditModalPropriedades = (value: NFeDto) => {
    setModalShowPropriedades(false);
    updateLocal<NFeDto>(STORAGENFE_KEY, value);
    navigate("/fiscal/nfe/cadastro");
  };

  const onDuplicateModalPropriedades = (value: NFeDto) => {
    setModalShowPropriedades(false);

    const itensOrdenados = (value.itens ? [...value.itens] : [])
      .sort((a, b) => {
        const seqA =
          a.sequencia != null && a.sequencia > 0
            ? a.sequencia
            : a.index != null && a.index > 0
            ? a.index
            : 0;
        const seqB =
          b.sequencia != null && b.sequencia > 0
            ? b.sequencia
            : b.index != null && b.index > 0
            ? b.index
            : 0;
        return seqA - seqB;
      })
      .map((item, index) => ({
        ...item,
        sequencia:
          item.sequencia != null && item.sequencia > 0
            ? item.sequencia
            : index + 1,
        index: index + 1,
      }));

    const duplicated: NFeDto = {
      ...value,
      numeroNF: 0,
      id: 0,
      itens: itensOrdenados,
    };

    updateLocal<NFeDto>(STORAGENFE_KEY, duplicated);
    navigate("/fiscal/nfe/cadastro");
  };

  const onEventModalPropriedades = (value: NFeDto) => {
    setModalShowPropriedades(false);
    navigate(`/fiscal/nfe/evento?nfeId=${value.id}`);
  };

  const onVisualizarModalPropriedades = (value: NFeDto) => {
    setModalShowPropriedades(false);
    navigate(`/fiscal/nfe/visualiza/${value.id}`, { state: { nfe: value } });
  };

  const onContinuarModalNovo = () => {
    setModalNovoShow(false);
    navigate("/fiscal/nfe/cadastro");
  };

  const onCriarNovoModalNovo = () => {
    setModalNovoShow(false);
    updateLocal<NFeDto>(STORAGENFE_KEY, null);
    navigate("/fiscal/nfe/cadastro");
  };

  const onCancelModalNovo = () => {
    setModalNovoShow(false);
  };

  return {
    data,
    loading,
    handleChangeSerie,
    handleSelectStatus,
    handleSetDates,
    useFilter,
    getDatePickerTitle,
    modalShowPropriedades,
    nfe,
    onReturnModalPropriedades,
    onDeleteModalPropriedades,
    onPrintModalPropriedades,
    onFetchStatusModalPropriedades,
    onDownloadXml,
    handleClickPropriedades,
    onSendModalPropriedades,
    onEditModalPropriedades,
    onDuplicateModalPropriedades,
    onEventModalPropriedades,
    onVisualizarModalPropriedades,
    modalNovoShow,
    onContinuarModalNovo,
    onCriarNovoModalNovo,
    onCancelModalNovo,
    menu,
  };
};

export default NFeCode;
